import React, { useEffect } from "react";
import { ArrowLeft, Shield, Eye, Database, MessageCircle, Phone, Mail } from "lucide-react";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";

const Datenschutz = () => {
  const navigate = useNavigate();
  
  // Set page-specific SEO
  useEffect(() => {
    document.title = "Datenschutzerklärung | Floria Maler & Sanierung";
    
    // Update meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Datenschutzerklärung von Floria Maler & Sanierung. Informationen zur Verarbeitung personenbezogener Daten gemäß DSGVO.');
    }
    
    // Update canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://malerbwfestpreis.de/datenschutz');
    }
    
    // Update OG tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', 'Datenschutzerklärung | Floria Maler & Sanierung');
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', 'https://malerbwfestpreis.de/datenschutz');
    
    // Cleanup on unmount
    return () => {
      document.title = "Maler Baden-Württemberg | Wohnung streichen zum Festpreis";
      if (metaDesc) {
        metaDesc.setAttribute('content', 'Maler in Baden-Württemberg zum Festpreis: Wohnung streichen inkl. Material und Abdeckarbeiten. Airless-Technik. Angebot schnell per WhatsApp anfragen.');
      }
      if (canonical) {
        canonical.setAttribute('href', 'https://malerbwfestpreis.de/');
      }
    };
  }, []);
  
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-orange-500 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-4">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-white hover:bg-white/20 mr-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Zurück
            </Button>
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold">Datenschutzerklärung</h1>
          <p className="text-blue-100 text-lg mt-2">Schutz Ihrer persönlichen Daten gemäß DSGVO</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Einleitung */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Shield className="mr-3 h-6 w-6 text-blue-600" />
            Datenschutz auf einen Blick
          </h2>
          
          <div className="space-y-4">
            <p className="text-gray-700">
              Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen 
              Daten passiert, wenn Sie unsere Website besuchen. Personenbezogene Daten sind alle Daten, 
              mit denen Sie persönlich identifiziert werden können.
            </p>
            
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-600">
              <h3 className="font-semibold text-gray-900 mb-2">Verantwortlicher:</h3>
              <p className="text-gray-700">
                Floria Maler & Sanierung<br/>
                Aixheimerstrasse 50/4<br/>
                78554 Aldingen<br/>
                Deutschland
              </p>
            </div>
          </div>
        </div>

        {/* Datenerfassung */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Database className="mr-3 h-6 w-6 text-blue-600" />
            Datenerfassung auf unserer Website
          </h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">Wer ist verantwortlich für die Datenerfassung?</h3>
              <p className="text-gray-700">
                Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. 
                Dessen Kontaktdaten können Sie dem Impressum dieser Website entnehmen.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">Wie erfassen wir Ihre Daten?</h3>
              <p className="text-gray-700 mb-3">
                Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. 
                Hierbei kann es sich z.B. um Daten handeln, die Sie in ein Kontaktformular eingeben.
              </p>
              <p className="text-gray-700">
                Andere Daten werden automatisch beim Besuch der Website durch unsere IT-Systeme erfasst. 
                Das sind vor allem technische Daten (z.B. Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs).
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">Wofür nutzen wir Ihre Daten?</h3>
              <ul className="text-gray-700 space-y-2">
                <li>• Zur Bereitstellung der Website und ihrer Funktionalitäten</li>
                <li>• Zur Bearbeitung von Anfragen über unser Kontaktformular</li>
                <li>• Zur direkten Kommunikation über WhatsApp</li>
                <li>• Zur Terminvereinbarung für unsere Malerdienstleistungen</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Kontaktformular */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Mail className="mr-3 h-6 w-6 text-blue-600" />
            Kontaktformular & WhatsApp
          </h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">Kontaktformular</h3>
              <p className="text-gray-700 mb-3">
                Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem 
                Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung 
                der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert.
              </p>
              <p className="text-gray-700">
                Diese Daten geben wir nicht ohne Ihre Einwilligung weiter. Die Verarbeitung der in das 
                Kontaktformular eingegebenen Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">WhatsApp Kontakt</h3>
              <p className="text-gray-700 mb-3">
                Wir bieten Ihnen die Möglichkeit, uns über WhatsApp zu kontaktieren. Wenn Sie uns über 
                WhatsApp kontaktieren, werden Ihre Nachrichtendaten über die Server von WhatsApp übertragen.
              </p>
              <p className="text-gray-700">
                Rechtsgrundlage für die Verarbeitung ist Art. 6 Abs. 1 lit. a DSGVO (Einwilligung). 
                Sie können Ihre Einwilligung jederzeit widerrufen.
              </p>
            </div>
          </div>
        </div>

        {/* Ihre Rechte */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Eye className="mr-3 h-6 w-6 text-blue-600" />
            Ihre Rechte
          </h2>
          
          <div className="space-y-4">
            <p className="text-gray-700 mb-4">
              Sie haben im Rahmen der geltenden gesetzlichen Bestimmungen jederzeit das Recht auf:
            </p>
            
            <ul className="text-gray-700 space-y-3">
              <li className="flex items-start">
                <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <span><strong>Auskunft</strong> über Ihre bei uns gespeicherten personenbezogenen Daten und deren Verarbeitung</span>
              </li>
              <li className="flex items-start">
                <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <span><strong>Berichtigung</strong> unrichtiger personenbezogener Daten</span>
              </li>
              <li className="flex items-start">
                <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <span><strong>Löschung</strong> Ihrer bei uns gespeicherten personenbezogenen Daten</span>
              </li>
              <li className="flex items-start">
                <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <span><strong>Einschränkung der Verarbeitung</strong> Ihrer personenbezogenen Daten</span>
              </li>
              <li className="flex items-start">
                <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <span><strong>Widerspruch</strong> gegen die Verarbeitung Ihrer personenbezogenen Daten</span>
              </li>
              <li className="flex items-start">
                <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <span><strong>Datenübertragbarkeit</strong>, sofern Sie in die Datenverarbeitung eingewilligt haben</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Kontakt für Datenschutz */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Phone className="mr-3 h-6 w-6 text-blue-600" />
            Kontakt in Datenschutzfragen
          </h2>
          
          <div className="space-y-4">
            <p className="text-gray-700 mb-4">
              Bei Fragen zum Datenschutz wenden Sie sich bitte an:
            </p>
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <Phone className="mr-3 h-5 w-5 text-gray-400" />
                <div>
                  <p className="font-semibold text-gray-900">Telefon:</p>
                  <a href="tel:+4915758183212" className="text-blue-600 hover:text-blue-700">
                    +49 157 58183212
                  </a>
                </div>
              </div>
              
              <div className="flex items-center">
                <MessageCircle className="mr-3 h-5 w-5 text-gray-400" />
                <div>
                  <p className="font-semibold text-gray-900">WhatsApp:</p>
                  <a href="https://wa.me/4915758183212" className="text-blue-600 hover:text-blue-700" target="_blank" rel="noopener noreferrer">
                    Direkt kontaktieren
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Beschwerderecht */}
        <div className="bg-orange-50 rounded-xl p-8 border border-orange-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Beschwerderecht</h2>
          <p className="text-gray-700">
            Unbeschadet eines anderweitigen verwaltungsrechtlichen oder gerichtlichen Rechtsbehelfs 
            steht Ihnen das Recht auf Beschwerde bei einer Aufsichtsbehörde, insbesondere in dem 
            Mitgliedstaat ihres gewöhnlichen Aufenthalts, ihres Arbeitsplatzes oder des Orts des 
            mutmaßlichen Verstoßes, zu, wenn Sie der Ansicht sind, dass die Verarbeitung der Sie 
            betreffenden personenbezogenen Daten gegen die DSGVO verstößt.
          </p>
        </div>

        {/* Stand */}
        <div className="text-center mt-12 pt-8 border-t border-gray-200">
          <p className="text-gray-500">
            Stand: {new Date().toLocaleDateString('de-DE', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Datenschutz;